import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import { CompanyType } from 'context/authentication/Company.types'
import { getSelectedCompany } from 'context/authentication/User.helpers'
import { analyticsEvent } from 'global/helpers/analytics'
import { isCompanyType } from 'global/helpers/authorization'
import { handleFormSubmission, processServerErrorMessages } from 'global/helpers/form'
import type { WithId } from 'global/helpers/requests'
import { type ReactElement, useEffect, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { deleteUser, revokeRole, USERS_QUERY_KEY } from './UserManager.service'
import type { RevokeRoleFormValues, UserData } from './UserManager.types'

interface DeleteUserDialogPayload {
  readonly user: UserData
}

interface DeleteUserDialogProps {
  readonly payload: DeleteUserDialogPayload
  readonly open: boolean
  readonly onAction: () => void
  readonly onClose: () => void
}

const DeleteUserDialog = ({ payload, open, onAction, onClose }: DeleteUserDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, formState, setError } = useForm()
  const queryClient = useQueryClient()
  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] }),
  })
  const revokeRoleMutation = useMutation({
    mutationFn: (data: WithId<RevokeRoleFormValues>) => revokeRole(data.id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] }),
  })
  const [deletionConfirmed, setDeletionConfirmed] = useState(false)

  useEffect(() => {
    setDeletionConfirmed(false)
  }, [payload])

  const onSubmit: SubmitHandler<unknown> = async (): Promise<void> => {
    if (isCompanyType(CompanyType.Operator)) {
      await handleFormSubmission(() => deleteUserMutation.mutateAsync(payload.user.cid), {
        onError: (error) => setError('root', processServerErrorMessages(error)),
        onSuccess: onAction,
      })
    } else {
      const selectedCompany = getSelectedCompany()
      if (!selectedCompany) {
        return
      }
      const userRole = payload.user.roles[selectedCompany.cid]
      if (!userRole) {
        return
      }
      await handleFormSubmission(() => revokeRoleMutation.mutateAsync({
        id: payload.user.cid,
        companyCid: selectedCompany.cid,
        role: userRole,
      }), {
        onError: (error) => setError('root', processServerErrorMessages(error)),
        onSuccess: () => {
          analyticsEvent('polestar_user_deleted')
          onAction()
        },
      })
    }
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={`${payload.user.displayName} — ${t('screens.shared.user_manager.card.actions.delete')}`}
      actions={
        <Button
          type="submit"
          color="error"
          variant="contained"
          loading={formState.isSubmitting}
          disabled={!deletionConfirmed}
          onClick={handleSubmit(onSubmit)}
        >
          {t('screens.shared.user_manager.card.actions.delete')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
      maxWidth="xs"
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Typography>
        {t('screens.shared.user_manager.delete_user.message')}
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            value={deletionConfirmed}
            checked={deletionConfirmed}
            onChange={(): void => setDeletionConfirmed(!deletionConfirmed)}
          />
        }
        label={t('shared.manager.deletion_confirm_checkbox')}
      />
    </KNDialog>
  )
}

export default DeleteUserDialog
